import { buildColorPalette } from './color.palette';

/**
 * Fixed, code-defined symbol system for the `assettype` field type.
 *
 * Unlike line symbols, an asset type has no separate "shape" layer to resolve against — the icon
 * already fully defines the glyph, so a type is simply `{ icon, color, title }`. `icon` is a Material
 * Icons font ligature (e.g. `light_mode`), stored as a plain string (not tied to any client-ui type),
 * mirroring how `FormFieldNestedListValues.icon` already stores an arbitrary icon string.
 *
 * A type is identified by its (unique) `title`; a row is a preset iff its title matches a
 * {@link DEFAULT_ASSET_TYPES} entry (so there is no stored "custom" flag — it is derived).
 */

/** Default color for an asset type with no explicit color (e.g. a freshly added custom type). */
export const DEFAULT_ASSET_TYPE_COLOR = '#202124';

/**
 * Title of the seeded "no particular asset type" preset — every field starts with this option selected.
 * Since it doesn't represent a real asset, map markers, the legend, KML, and shapefile all treat a
 * collection with this title selected the same as having no asset type at all (@see CollectionModel.assetType).
 */
export const DEFAULT_ASSET_TYPE_TITLE = 'Collection';

/** Icon of the seeded "Collection" preset. Custom asset types start with no icon — the designer must pick one. */
export const COLLECTION_ASSET_TYPE_ICON = 'radio_button_unchecked';

export interface AssetTypeIcon {
	/** Material Icons font ligature — the published, additive-only icon id. */
	id: string;
	name: string;
}

/**
 * The curated set of icons offered in the icon picker, chosen for utility/pole asset symbology.
 * Every icon used by a {@link DEFAULT_ASSET_TYPES} preset must appear here. Additive-only, like
 * `LINE_SYMBOL_SHAPES` — removing an id also requires deleting its generated pin SVG (see
 * `packages/core-node/src/export/assets/asset-type-icons/README.md`).
 */
export const ASSET_TYPE_ICONS: readonly AssetTypeIcon[] = [
	{ id: 'radio_button_unchecked', name: 'Radio Button Unchecked' },
	{ id: 'highlight_off', name: 'Highlight Off' },
	{ id: 'light_mode', name: 'Light Mode' },
	{ id: 'gps_fixed', name: 'GPS Fixed' },
	{ id: 'pause_presentation', name: 'Pause Presentation' },
	{ id: 'rectangle', name: 'Rectangle' },
	{ id: 'surround_sound', name: 'Surround Sound' },
	{ id: 'hourglass_top', name: 'Hourglass Top' },
	{ id: 'view_stream', name: 'View Stream' },
	{ id: 'border_all', name: 'Border All' },
	{ id: 'stop_circle', name: 'Stop Circle' },
	{ id: 'hexagon', name: 'Hexagon' },
	{ id: 'eject', name: 'Eject' },
	{ id: 'swipe_down_alt', name: 'Swipe Down Alt' },
	{ id: 'network_cell', name: 'Network Cell' },
	{ id: 'disabled_by_default', name: 'Disabled By Default' },
	{ id: 'home', name: 'Home' },
	{ id: 'villa', name: 'Villa' },
	{ id: 'view_column', name: 'View Column' },
	{ id: 'fire_hydrant_alt', name: 'Fire Hydrant Alt' },
	{ id: 'emergency', name: 'Emergency' },
	{ id: 'center_focus_strong', name: 'Center Focus Strong' },
	{ id: 'star', name: 'Star' },
	{ id: 'square', name: 'Square' },
	{ id: 'pentagon', name: 'Pentagon' },
	{ id: 'details', name: 'Details' },
	{ id: 'commit', name: 'Commit' },
	{ id: 'texture', name: 'Texture' },
	{ id: 'place', name: 'Place' },
	{ id: 'nature', name: 'Nature' },
	{ id: 'games', name: 'Games' },
	{ id: 'api', name: 'API' },
	{ id: 'electric_bolt', name: 'Electric Bolt' },
	{ id: 'park', name: 'Park' },
	{ id: 'invert_colors', name: 'Invert Colors' },
	{ id: 'local_fire_department', name: 'Fire Department' },
	{ id: 'flag', name: 'Flag' },
	{ id: 'settings_input_antenna', name: 'Settings Input Antenna' },
	{ id: 'wifi_2_bar', name: 'Wifi 2 Bar' },
	{ id: 'sensors', name: 'Sensors' },
];

export type AssetTypeGroup = 'general' | 'structure' | 'electrical' | 'comms' | 'env';

export interface DefaultAssetType {
	icon: string;
	color: string;
	title: string;
	group: AssetTypeGroup;
	colorEditable?: boolean;
	visibilityEditable?: boolean;
	/** Degrees clockwise to rotate the icon glyph (e.g. an hourglass turned on its side for a valve). */
	rotate?: number;
}

/** The preset asset-type library seeded onto every new asset type field (in display order), all black by default. */
export const DEFAULT_ASSET_TYPES: readonly DefaultAssetType[] = [
	{
		icon: COLLECTION_ASSET_TYPE_ICON,
		color: DEFAULT_ASSET_TYPE_COLOR,
		title: DEFAULT_ASSET_TYPE_TITLE,
		group: 'general',
		colorEditable: false,
		visibilityEditable: false,
	},
	{ icon: 'highlight_off', color: DEFAULT_ASSET_TYPE_COLOR, title: 'Utility Pole', group: 'general' },
	{ icon: 'light_mode', color: DEFAULT_ASSET_TYPE_COLOR, title: 'Street Light', group: 'general' },
	{ icon: 'gps_fixed', color: DEFAULT_ASSET_TYPE_COLOR, title: 'Control/Reference Point', group: 'general' },
	{ icon: 'pause_presentation', color: DEFAULT_ASSET_TYPE_COLOR, title: 'Cabinet', group: 'structure' },
	{ icon: 'rectangle', color: DEFAULT_ASSET_TYPE_COLOR, title: 'Hand Hole', group: 'structure' },
	{ icon: 'surround_sound', color: DEFAULT_ASSET_TYPE_COLOR, title: 'Manhole', group: 'structure' },
	{ icon: 'hourglass_top', color: DEFAULT_ASSET_TYPE_COLOR, title: 'Valve', group: 'structure', rotate: 90 },
	{ icon: 'view_stream', color: DEFAULT_ASSET_TYPE_COLOR, title: 'Vault', group: 'structure' },
	{ icon: 'border_all', color: DEFAULT_ASSET_TYPE_COLOR, title: 'Junction Box', group: 'structure' },
	{ icon: 'stop_circle', color: DEFAULT_ASSET_TYPE_COLOR, title: 'Meter', group: 'structure' },
	{ icon: 'hexagon', color: DEFAULT_ASSET_TYPE_COLOR, title: 'Bore Pit', group: 'structure' },
	{ icon: 'eject', color: DEFAULT_ASSET_TYPE_COLOR, title: 'Transformer', group: 'electrical' },
	{ icon: 'swipe_down_alt', color: DEFAULT_ASSET_TYPE_COLOR, title: 'Anchor', group: 'electrical' },
	{ icon: 'network_cell', color: DEFAULT_ASSET_TYPE_COLOR, title: 'Push Brace', group: 'electrical' },
	{ icon: 'disabled_by_default', color: DEFAULT_ASSET_TYPE_COLOR, title: 'Pedestal', group: 'comms' },
	{ icon: 'home', color: DEFAULT_ASSET_TYPE_COLOR, title: 'Residential Structure', group: 'env' },
	{ icon: 'villa', color: DEFAULT_ASSET_TYPE_COLOR, title: 'Commercial Structure', group: 'env' },
	{ icon: 'view_column', color: DEFAULT_ASSET_TYPE_COLOR, title: 'Storm Inlet', group: 'env' },
	{ icon: 'fire_hydrant_alt', color: DEFAULT_ASSET_TYPE_COLOR, title: 'Fire Hydrant', group: 'env' },
	{ icon: 'emergency', color: DEFAULT_ASSET_TYPE_COLOR, title: 'Vegetation', group: 'env' },
];

export const ASSET_TYPE_GROUP_ORDER: readonly AssetTypeGroup[] = ['general', 'structure', 'electrical', 'comms', 'env'];

export const ASSET_TYPE_GROUP_LABEL: Readonly<Record<AssetTypeGroup, string>> = {
	general: 'General',
	structure: 'Shared Structure',
	electrical: 'Electrical',
	comms: 'Communications',
	env: 'Environment/Other',
};

const DEFAULT_ASSET_TYPE_BY_TITLE: Readonly<Record<string, DefaultAssetType>> = DEFAULT_ASSET_TYPES.reduce<
	Record<string, DefaultAssetType>
>((acc, type) => {
	acc[type.title] = type;
	return acc;
}, {});

/** Whether a title belongs to the preset library — i.e. it is a default (non-custom) asset type. */
export function isDefaultAssetTypeTitle(title: string): boolean {
	return DEFAULT_ASSET_TYPE_BY_TITLE[title] != null;
}

export function isColorEditableDefaultAssetType(title: string): boolean {
	const preset = DEFAULT_ASSET_TYPE_BY_TITLE[title];
	return preset == null || preset.colorEditable !== false;
}

export function isVisibilityEditableDefaultAssetType(title: string): boolean {
	const preset = DEFAULT_ASSET_TYPE_BY_TITLE[title];
	return preset == null || preset.visibilityEditable !== false;
}

/** Degrees clockwise to rotate a preset's icon glyph. 0 for a custom (non-preset) title, or a preset with no rotation. */
export function rotationForDefaultAssetType(title: string): number {
	return DEFAULT_ASSET_TYPE_BY_TITLE[title]?.rotate ?? 0;
}

/** The picker group for an asset type, resolved by title; custom titles fall under `general`. */
export function assetTypeGroup(title: string): AssetTypeGroup {
	return DEFAULT_ASSET_TYPE_BY_TITLE[title]?.group ?? 'general';
}

/**
 * Curated quick-pick colors for the asset type customizer: a base swatch set plus every color a preset
 * asset type already uses. Not exhaustive — the customizer also offers a native picker for any other color.
 */
export const ASSET_TYPE_COLOR_PALETTE: readonly string[] = buildColorPalette(
	[
		'#202124',
		'#6b7075',
		'#8a5a2b',
		'#e01e1e',
		'#e8821e',
		'#a89400',
		'#1fa11f',
		'#1b9e91',
		'#2a9fd6',
		'#2f6fe0',
		'#7a5af8',
		'#d61fbf',
	],
	DEFAULT_ASSET_TYPES.map((type) => type.color)
);
